import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Grid2,
  Box,
  Typography,
  TextField,
  Button,
  Modal,
  InputLabel,
  Tabs,
  Tab,
  Paper,
  Checkbox,
  FormControlLabel
} from "@mui/material";
import { useForm } from "react-hook-form";
import ModalBox from "../../themes/overrides/ModalBox";
import OutlinedInput from "../../themes/overrides/OutlinedInput";
import LoadingOverlay from "../../components/LoadingOverlay";
import { addApp, updateApp } from "../../actions/appStudioAction";

const AddApp = ({ isOpen, onClose, onSubmitSuccess, editMode, app }) => {
  const dispatch = useDispatch();
  const workspaceId = useSelector((state) => state.workspace.workspaceID) || localStorage.getItem("workspaceID");
  const [loading, setLoading] = useState(false);

  const [selectedTab, setSelectedTab] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);

  const [mainChecked, setMainChecked] = useState(false);
  const [subChecked, setSubChecked] = useState(false);
  const [appLocations, setAppLocations] = useState({
    sideBar: { checked: false },
    textField: { checked: false, subCheckboxes: ['object'] },
  });

  const handleMainCheckboxChange = (mainKey) => (event) => {
    setAppLocations((prevState) => ({
      ...prevState,
      [mainKey]: {
        ...prevState[mainKey],
        checked: event.target.checked,
        // Reset sub-checkboxes if main checkbox is unchecked
        ...(prevState[mainKey].subCheckboxes && !event.target.checked
          ? { subCheckboxes: [] }
          : {}),
      },
    }));
  };

  const handleSubCheckboxChange = (mainKey, subKey) => (event) => {
    setAppLocations((prevState) => {
      const subCheckboxes = prevState[mainKey].subCheckboxes.includes(subKey)
        ? prevState[mainKey].subCheckboxes.filter((key) => key !== subKey)
        : [...prevState[mainKey].subCheckboxes, subKey];

      return {
        ...prevState,
        [mainKey]: {
          ...prevState[mainKey],
          subCheckboxes,
        },
      };
    });
  };

  const getSimplifiedState = () => {
    return Object.keys(appLocations).reduce((acc, key) => {
      const { checked, subCheckboxes } = appLocations[key];
      if (checked) {
        acc[key] = subCheckboxes?.length ? subCheckboxes : true;
      }
      return acc;
    }, {});
  };

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    unregister,
    formState: { errors },
  } = useForm({
    defaultValues: {
      appName: "",
      url: "",
      logo: null,
      locations: {},
    },
  });

  const [error, setError] = useState("");

  useEffect(() => {
    console.log(app);

    if (editMode && app) {
      setValue("appName", app.appName);
      setValue("url", app.url);
      setValue("logo", app.logo);

      if (app.logo) {
        setPreview(app.logo);
      }

      if (app.locations) {
        const initialLocations = {
          sideBar: { checked: false },
          textField: { checked: false, subCheckboxes: ['object'] },
        };

        Object.entries(app.locations).forEach(([key, value]) => {
          initialLocations[key] = {
            checked: true,
            subCheckboxes: Array.isArray(value) ? value : ['object']
          };
        });

        setAppLocations(initialLocations);
      }


    } else {
      reset();
    }
  }, [editMode, app]);


  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = () => {
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const files = e.dataTransfer.files;
    handleFileSelect(files);
  };

  const handleFileSelect = (files) => {
    if (files?.[0]) {
      setSelectedFile(files[0]);
      setValue('file', files);

      if (files[0].type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result);
        };
        reader.readAsDataURL(files[0]);
      } else {
        setPreview(null);
      }
      unregister('file');
    }
  };


  const handleInputChange = (e) => {
    handleFileSelect(e.target.files);
  };

  const onSubmit = async (data) => {
    if (!data.appName) {
      setError("App Name is required.");
      return;
    }

    if (!data.url) {
      setError("URL is required.");
      return;
    }

    data.locations = getSimplifiedState(appLocations);
    setLoading(true);
    try {
      if (editMode) {
        const editFormData = new FormData();
        editFormData.append('appName', data.appName);
        editFormData.append('url', data.url);

        if (selectedFile) {
          editFormData.append('logo', selectedFile);
        }

        const success = await dispatch(updateApp(app._id, editFormData));
        if (success) {
          onSubmitSuccess(success);
          reset();
          onClose();
        }
      } else {
        const success = await dispatch(addApp(data));

        if (success?.message === 'Filename already exist') {
          setError('File already exists');
          return;
        }

        if (success) {
          onSubmitSuccess(success);
          setSelectedFile(null);
          setPreview(null);
          reset();
          onClose();
        }
      }
    } catch (error) {
      setError("Upload Failed, Please try again.");
    } finally {
      setLoading(false);
    }
  };

  function camelToNormal(str) {
    return str
      .replace(/([A-Z])/g, " $1") // Add a space before each uppercase letter
      .replace(/^./, char => char.toUpperCase()); // Capitalize the first character
  }


  return (
    <>
      <Modal open={isOpen} onClose={onClose}>
        <ModalBox
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            width: { xs: '95%', sm: '600px', md: '700px' },
            maxHeight: { xs: '95vh', sm: '90vh' },
          }}
        >
          <Typography variant="h2" sx={{ marginBottom: 2 }}>
            {editMode ? "Edit App" : "Add App"}
          </Typography>
          <InputLabel shrink htmlFor="appName">
            App Name
          </InputLabel>
          <OutlinedInput
            fullWidth
            name="appName"
            {...register("appName", { required: "App Name is required" })}
            error={!!errors.appName}
            helperText={errors.appName?.message}
          />
          <InputLabel shrink htmlFor="url">
            URL
          </InputLabel>
          <OutlinedInput
            fullWidth
            name="url"
            type="url"
            {...register("url")}
            maxRows={4}
          />
          {editMode ? (
            <Typography>{app?.appName}</Typography>
          ) : (
            <>
              <Box>
                <Typography sx={{ mt: 3, mb: 2 }}>Dynamic Checkboxes</Typography>

                {Object.keys(appLocations)?.map((mainKey) => (
                  <Box key={mainKey} sx={{ mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={appLocations[mainKey].checked}
                          onChange={handleMainCheckboxChange(mainKey)}
                        />
                      }
                      label={camelToNormal(mainKey)}
                    />
                    {/* Render Sub-Checkboxes */}
                    {appLocations[mainKey].checked && appLocations[mainKey].subCheckboxes && (
                      <Box sx={{ ml: 4 }}>
                        {['object'].map((subKey) => (
                          <FormControlLabel
                            key={subKey}
                            control={
                              <Checkbox
                                checked={appLocations[mainKey].subCheckboxes.includes(subKey)}
                                onChange={handleSubCheckboxChange(mainKey, subKey)}
                              />
                            }
                            label={camelToNormal(subKey)}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>

            </>
          )}
          {error && (
            <Typography variant="body2" color="error" sx={{ marginBottom: 2 }}>
              {error}
            </Typography>
          )}
          <Button type="submit" fullWidth variant="contained" color="primary" sx={{ mt: 2 }}>
            Publish
          </Button>
          {loading && <LoadingOverlay />}
        </ModalBox>
      </Modal>
    </>
  );
};

export default AddApp;
